import type { CSSObject } from '@mui/material/styles';

/**
 * Shared column sizing for the two-column reservation form (form on the left,
 * benefits/support aside on the right).
 *
 * The aside used to be a fixed, non-shrinking 320px that jumped to 550px at
 * `lg`, so every pixel the dialog's gutters took came out of the form beside
 * it: at >=1200px the form was left with 298px and its date/time row — which
 * needs ~395px to stay on one line — wrapped into a squished stack
 * (F1V2-6149). Sizing the aside against its container instead keeps both
 * columns usable at every width in between.
 */

/** Viewport at which the aside moves from under the form to beside it. */
const TWO_COLUMN_MIN_VIEWPORT = 768;

/**
 * The two widths the design pins the aside to: 320px when the container is
 * 736px (a 768px viewport, the narrowest two-column layout) and 550px — what
 * `PassengerBenefits` rows are laid out for — when it is 1152px (1440px up).
 */
const NARROW_ANCHOR = { container: 736, aside: 320 };
const WIDE_ANCHOR = { container: 1152, aside: 550 };

const slope =
  (WIDE_ANCHOR.aside - NARROW_ANCHOR.aside) /
  (WIDE_ANCHOR.container - NARROW_ANCHOR.container);
const offset = NARROW_ANCHOR.aside - slope * NARROW_ANCHOR.container;

/**
 * Every container width between the anchors lands on the line through them —
 * a percentage of the container plus an offset — so the aside grows with the
 * dialog instead of jumping at a breakpoint and taking the jump out of the
 * form.
 */
const ASIDE_WIDTH = `clamp(${NARROW_ANCHOR.aside}px, calc(${(slope * 100).toFixed(2)}% ${
  offset < 0 ? '-' : '+'
} ${Math.abs(offset).toFixed(1)}px), ${WIDE_ANCHOR.aside}px)`;

/**
 * Form column: grows into whatever the aside leaves. Its flex basis is what
 * the flex container measures when deciding to wrap, so a container too narrow
 * for both drops the aside below the form instead of crushing it.
 */
const FORM_COLUMN_BASIS = 360;

export const formColumnStyles: CSSObject = {
  flex: `1 1 ${FORM_COLUMN_BASIS}px`,
};

/**
 * Aside column: full width and stacked under the form below `twoColumnFrom`,
 * then beside it at a width that tracks the container.
 *
 * `twoColumnFrom` is a parameter because the legacy (pre-refactor) dialog's
 * container only turns into a row at `md` — sizing the aside as a column
 * before then would leave it a narrow block under a full-width form.
 */
export const asideColumnStyles = (
  twoColumnFrom: number = TWO_COLUMN_MIN_VIEWPORT,
): CSSObject => ({
  width: '100%',
  minWidth: 0,
  [`@media (min-width: ${twoColumnFrom}px)`]: {
    width: ASIDE_WIDTH,
    flexShrink: 0,
  },
});
